import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowDown,
  Bot,
  Check,
  ChevronDown,
  Code,
  Copy,
  FileText,
  Folder,
  Globe,
  GripVertical,
  Hammer,
  History,
  Layout,
  Maximize2,
  MessageSquare,
  Minimize2,
  Paperclip,
  Pencil,
  Plus,
  Search,
  Send,
  Terminal,
  Trash2,
  X,
  Zap
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDropzone } from 'react-dropzone';
import ObexRequestHandler from '../../handlers/ObexRequestsHandler';
import { useBrunixStream } from '../../hooks/useBrunixStream';
import { PlanStep } from '../../services/brunixStream';
import SessionService from '../../services/SessionsService';
import ConfirmationModal from './ConfirmationModal';
import Modal from '../shared/Modal/Modal';
// Keep above the BrunixMessageList import: AiBrunixPanel.css comes in through it and
// overrides some of the bx-* rules defined here.
import './css_v2/BrunixSidebar.css';

import BrunixMessageList from './BrunixMessageList';

const BX_CONTEXT_FILE_ACCEPT: Record<string, string[]> = {
  'text/*': ['.txt', '.json', '.md'],
};

// TEMP-SEL_PROJECT: nothing assigns SEL_PROJECT, so it stays 0 and the backend rejects it as
// missing ("org_id, project_id, name and content are required"). Falls back until there's a
// default project to point at.
const bxProjectId = () => SessionService.SEL_PROJECT || 1;

// ── Types ────────────────────────────────────────────────────────────────────
import { Context, HistoryItem, McpServer } from './BrunixMessageList/brunix';



// ── Plan state diagram helpers ────────────────────────────────────────────────

type FlowGroup =
  | { kind: 'check'; check: PlanStep; checkIdx: number; branches: Array<{ step: PlanStep; idx: number }> }
  | { kind: 'action'; step: PlanStep; idx: number };

function groupPlanSteps(steps: PlanStep[]): FlowGroup[] {
  const groups: FlowGroup[] = [];
  let i = 0;
  while (i < steps.length) {
    const step = steps[i];
    if (step.node_type === 'check') {
      const branches: Array<{ step: PlanStep; idx: number }> = [];
      let j = i + 1;
      while (j < steps.length && (steps[j].node_type === 'conditional' || steps[j].node_type === 'followup')) {
        branches.push({ step: steps[j], idx: j });
        j++;
      }
      groups.push({ kind: 'check', check: step, checkIdx: i, branches });
      i = j;
    } else {
      groups.push({ kind: 'action', step, idx: i });
      i++;
    }
  }
  return groups;
}

function planStepLabel(step: PlanStep): string {
  const raw = step.reason ?? step.tool ?? '';
  const colon = raw.indexOf(':');
  const text = (colon !== -1 && colon < 9) ? raw.slice(colon + 1).trim() : raw;
  return text.length > 64 ? text.slice(0, 61) + '…' : (text || step.tool);
}

function toolShort(tool: string): string {
  return tool.replace(/^internal__/, '').replace(/_/g, ' ');
}

function DiagArrow({ small, label, amber }: { small?: boolean; label?: string; amber?: boolean }) {
  const cls = [
    'bx-diag-arrow',
    small ? 'bx-diag-arrow--sm' : '',
    amber ? 'bx-diag-arrow--amber' : '',
  ].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <div className="bx-diag-arrow-line" />
      {label && <span className="bx-diag-arrow-label">{label}</span>}
      <div className="bx-diag-arrow-head" />
    </div>
  );
}

function PlanFlowDiagram({ steps }: { steps: PlanStep[] }) {
  const groups = groupPlanSteps(steps);
  return (
    <div className="bx-diag">
      {groups.map((g, gi) => (
        <React.Fragment key={gi}>
          {gi > 0 && <DiagArrow />}
          {g.kind === 'action' ? (
            <div className="bx-diag-node bx-diag-node--action">
              <div className="bx-diag-node-dot" />
              <div className="bx-diag-node-body">
                <span className="bx-diag-node-label">{planStepLabel(g.step)}</span>
                <span className="bx-diag-node-tool">{toolShort(g.step.tool)}</span>
              </div>
            </div>
          ) : (
            <div className="bx-diag-decision">
              {/* Decision node */}
              <div className="bx-diag-node bx-diag-node--check">
                <div className="bx-diag-diamond">◆</div>
                <div className="bx-diag-node-body">
                  <span className="bx-diag-node-label">{planStepLabel(g.check)}</span>
                  <span className="bx-diag-node-tool">{toolShort(g.check.tool)}</span>
                </div>
                {g.branches.length > 0 && (
                  <span className="bx-diag-skip-badge">found → skip</span>
                )}
              </div>
              {/* Conditional branch (if not found) */}
              {g.branches.length > 0 && (
                <>
                  <DiagArrow label="if not found" amber />
                  <div className="bx-diag-branch">
                    {g.branches.map((b, bi) => (
                      <React.Fragment key={b.idx}>
                        {bi > 0 && <DiagArrow small />}
                        <div className="bx-diag-node bx-diag-node--branch">
                          <div className="bx-diag-node-dot bx-diag-node-dot--branch" />
                          <div className="bx-diag-node-body">
                            <span className="bx-diag-node-label">{planStepLabel(b.step)}</span>
                            <span className="bx-diag-node-tool">{toolShort(b.step.tool)}</span>
                          </div>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Tool label helpers ────────────────────────────────────────────────────────
const TOOL_LABELS: Record<string, string> = {
  developer_teams: 'Developer Teams',
  developer_projects: 'Developer Projects',
  iam__get_roles: 'Get IAM Roles',
  iam__update_team_roles: 'Update Team Roles',
  create_dev_team: 'Create Developer Team',
  update_developer_teams: 'Update Developer Teams',
  devs_team: 'Developer Team Members',
  projects: 'Projects',
  api_catalog__init_repo: 'Initialize API Repository',
  api_catalog__push_repo: 'Push to API Repository',
  api_catalog__list: 'List API Catalog',
  api_catalog__get: 'Get API from Catalog',
  unittest__config: 'Configure Unit Tests',
  unittest__documentation: 'Unit Test Documentation',
  unittest__run: 'Run Unit Tests',
  publishing__publish_virtual_api: 'Publish Virtual API',
  publishing__unpublish_api: 'Unpublish API',
  publishing__list: 'List Publications',
};

function fmtId(id?: string): string {
  if (!id) return '';
  return id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function fmtTool(raw: string): string {
  const key = raw.replace(/^internal__/, '');
  if (TOOL_LABELS[key]) return TOOL_LABELS[key];
  return key.replace(/__/g, ' · ').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ── Plan step list (replaces PlanFlowDiagram) ────────────────────────────────
function PlanStepList({ steps }: { steps: PlanStep[] }) {
  let actionCount = 0;
  return (
    <ol className="bx-step-list">
      {steps.map((step, i) => {
        const isCheck = step.node_type === 'check';
        const isBranch = step.node_type === 'conditional' || step.node_type === 'followup';
        if (!isCheck && !isBranch) actionCount++;
        const num = isCheck ? '◆' : isBranch ? '↳' : String(actionCount);
        const raw = step.reason ?? '';
        const colon = raw.indexOf(':');
        const desc = (colon !== -1 && colon < 9) ? raw.slice(colon + 1).trim() : raw;
        return (
          <li
            key={i}
            className={`bx-step-item${isCheck ? ' bx-step-item--check' : isBranch ? ' bx-step-item--branch' : ''}`}
          >
            <span className="bx-step-num">{num}</span>
            <div className="bx-step-body">
              <span className="bx-step-tool">{fmtTool(step.tool)}</span>
              {desc && <span className="bx-step-desc">{desc}</span>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ── Playbook / Template collapsible chips ────────────────────────────────────

function PlaybookCard({ processId }: { processId?: string }) {
  return (
    <div className="bx-proc-chip" style={{ cursor: 'default' }}>
      <span className="bx-eval-icon">📖</span>
      <span className="bx-proc-chip-label">Playbook: <strong>{fmtId(processId)}</strong></span>
    </div>
  );
}

function TemplateCard({ processId, tools }: { processId?: string; tools?: string[] }) {
  const [open, setOpen] = useState(false);
  const count = tools?.length ?? 0;
  return (
    <div>
      <div className="bx-proc-chip" onClick={() => count > 0 && setOpen(o => !o)}>
        <span className="bx-eval-icon">📋</span>
        <span className="bx-proc-chip-label">Template: <strong>{fmtId(processId)}</strong></span>
        {count > 0 && !open && (
          <span className="bx-proc-chip-count">· {count} steps</span>
        )}
        {count > 0 && (
          <ChevronDown size={11} className={`bx-proc-chevron${open ? ' bx-proc-chevron--open' : ''}`} />
        )}
      </div>
      {open && count > 0 && (
        <ul className="bx-proc-tools">
          {tools!.map((t, idx) => (
            <li key={idx} className="bx-proc-tool-item">{t}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────
export default function BrunixSidebar({
  isOpen, onClose, isSidebarCollapsed = false, onWidthChange, userId
}: {
  isOpen: boolean; onClose: () => void;
  isSidebarCollapsed?: boolean; onWidthChange?: (width: number) => void;
  userId?: string | number;
}) {

  const [input, setInput] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedLLM, setSelectedLLM] = useState('Emi 3.15');
  const [isLLMDropdownOpen, setIsLLMDropdownOpen] = useState(false);
  const [llmDropdownPos, setLlmDropdownPos] = useState({ top: 0, left: 0 });
  const [isNewChatDropdownOpen, setIsNewChatDropdownOpen] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [activeTab, setActiveTab] = useState<'chat' | 'history' | 'contexts' | 'mcp'>('chat');
  const [isMaximized, setIsMaximized] = useState(false);

  const [contextDeleteOpen, setContextDeleteOpen] = useState(false);
  const [contextToDelete, setContextToDelete] = useState<any>(null);

  // Modal de confirmación para borrado MCP
  const [mcpDeleteOpen, setMcpDeleteOpen] = useState(false);
  const [mcpToDelete, setMcpToDelete] = useState<any>(null);

  const [historyToDelete, setHistoryToDelete] = useState<string | null>(null);

  // Local state — there's no app-wide context here to share it with the dashboard layout,
  // so maximizing doesn't collapse the sidebar.
  const [isBrunixMaximized, setIsBrunixMaximized] = useState(false);
  const [, setIsSidebarCollapsed] = useState(false);
  const [sidebarStateBeforeMaximize, setSidebarStateBeforeMaximize] = useState(false);

  const [brunixSidebarWidth, setBrunixSidebarWidth] = useState(384);
  const sidebarWidth = brunixSidebarWidth;
  const setSidebarWidth = setBrunixSidebarWidth;

  //const [isResizing, setIsResizing] = useState(false);
  const [chatMode, setChatMode] = useState<'chat' | 'codex' | 'builder' | 'architect'>('chat');
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [currentChatId, setCurrentChatId] = useState<string>(Date.now().toString());
  const [contexts, setContexts] = useState<Context[]>([]);
  const [contextsLoading, setContextsLoading] = useState(false);
  const [contextsError, setContextsError] = useState<string | null>(null);
  const [mcpServers, setMcpServers] = useState<McpServer[]>([]);
  const [mcpLoading, setMcpLoading] = useState(false);
  const [mcpError, setMcpError] = useState<string | null>(null);

  const {
    messages,
    setMessages,
    isStreaming,
    streamingMsgId,
    llmCalling,
    streamJustFinished,
    lastStepReached,
    pendingConfirmation,
    setPendingConfirmation,
    slotInputs, setSlotInputs,
    stepSelections, setStepSelections,
    retryArgsText, setRetryArgsText,
    isTyping,
    sendMessage, confirmAction, retryAction, skipAction, cancelStream, reset
  } = useBrunixStream({
    sessionId: currentChatId,
    initialMessages: [{
      // 'system' renders through SystemMessage instead of a regular reply bubble.
      id: '1', role: 'system',
      content: `Bienvenido, ${SessionService.User?.name || SessionService.User?.first_name || SessionService.User?.username || SessionService.CLIENT_USERNAME || 'Usuario'}. Soy Emi, tu asistente de inteligencia logística. ¿En qué puedo ayudarte hoy?`,
      timestamp: new Date(), isWelcome: true,
    }],
    contexts,
    mcpServers
  });
  const [isAddingContext, setIsAddingContext] = useState(false);
  const [newContextName, setNewContextName] = useState('');
  const [newContextDescription, setNewContextDescription] = useState('');
  const [newContextValue, setNewContextValue] = useState('');
  const [selectedContextFileName, setSelectedContextFileName] = useState('');
  const [newContextType, setNewContextType] = useState<'file' | 'url' | 'text' | 'project'>('text');
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [contextSourceType, setContextSourceType] = useState<'external' | 'internal'>('external');
  const [internalContexts, setInternalContexts] = useState<any[]>([]);
  const [internalContextsLoading, setInternalContextsLoading] = useState(false);
  const [selectedInternalContext, setSelectedInternalContext] = useState<any | null>(null);
  const [editingContext, setEditingContext] = useState<Context | null>(null);
  const [editContextName, setEditContextName] = useState('');
  const [editContextDescription, setEditContextDescription] = useState('');
  const [isAddDropdownOpen, setIsAddDropdownOpen] = useState(false);
  const [isAddingMcp, setIsAddingMcp] = useState(false);
  const [newMcpName, setNewMcpName] = useState('');
  const [newMcpUrl, setNewMcpUrl] = useState('');
  const [newMcpDescription, setNewMcpDescription] = useState('');
  const [newMcpBearer, setNewMcpBearer] = useState('');
  const [mcpSourceType, setMcpSourceType] = useState<'external' | 'internal'>('external');
  const [internalMcpServers, setInternalMcpServers] = useState<any[]>([]);
  const [internalMcpLoading, setInternalMcpLoading] = useState(false);
  const [selectedInternalMcp, setSelectedInternalMcp] = useState<any | null>(null);
  const [editingMcp, setEditingMcp] = useState<McpServer | null>(null);
  const [editMcpName, setEditMcpName] = useState('');
  const [editMcpUrl, setEditMcpUrl] = useState('');
  const [editMcpDescription, setEditMcpDescription] = useState('');
  const [editMcpBearer, setEditMcpBearer] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [matches, setMatches] = useState<{ messageId: string }[]>([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth); // ← aquí

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /*const projectContexts = [
    { id: 'p1', name: 'Auth Module', description: 'Authentication and authorization logic' },
    { id: 'p2', name: 'Database Schema', description: 'Prisma schema and migrations' },
    { id: 'p3', name: 'API Endpoints', description: 'REST and GraphQL endpoints' },
    { id: 'p4', name: 'UI Components', description: 'Shared React components' }
  ];*/
  const LLMS = [
    { id: 'brunix', name: 'Emi 3.15', provider: 'BETA', icon: Bot },
    //{ id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', provider: 'OpenAI' },
    //{ id: 'gpt-4o-mini', name: 'GPT-4o Mini', provider: 'OpenAI' },
  ];
  const modeOptions = [
    { id: 'chat', name: 'Chat', icon: MessageSquare },
    { id: 'codex', name: 'Codex', icon: Code },
    { id: 'builder', name: 'Builder', icon: Hammer },
    { id: 'architect', name: 'Architect', icon: Layout },
  ];
  const typeOptions = [
    { id: 'text', name: 'Text', icon: FileText },
    { id: 'file', name: 'File', icon: Paperclip },
    { id: 'url', name: 'URL', icon: Globe },
    //{ id: 'project', name: 'Contexts Project', icon: Zap },
  ];

  const socketRef = useRef<WebSocket | null>(null);
  const grpcAbortRef = useRef<AbortController | null>(null);
  const abortHandlerRef = useRef<(() => void) | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const llmBtnRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const matchRefs = useRef<{ [key: number]: HTMLSpanElement | null }>({});
  const matchOrdinalRef = useRef(0);
  const newChatRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  const addDropdownRef = useRef<HTMLDivElement>(null);
  const hasOpenedOnce = useRef(false);  // ← aquí
  const loadedForRef = useRef<string | null>(null); // tracks "org_id:project_id" already loaded


  const minWidth = 400;
  const sidebarOffset = isSidebarCollapsed ? 76 : 250;

  // ── Detección automática de tipo por contenido ────────────────────────────
  // No depende de localStorage: lee el valor guardado y deduce el tipo de contexto.
  const detectContextType = (value: string): Context['type'] => {
    if (!value) return 'text';
    const trimmed = value.trim();

    // URL: empieza con http:// o https://
    if (/^https?:\/\//i.test(trimmed)) return 'url';

    // File: empieza con / (Unix) o letra:\ (Windows), o tiene extensión de archivo
    if (/^\/|^[a-zA-Z]:\\/.test(trimmed)) return 'file';
    if (/\.[a-zA-Z0-9]{1,6}$/.test(trimmed) && !trimmed.includes(' ')) return 'file';

    return 'text';
  };

  // ── Contexts API helpers ──────────────────────────────────────────────────
  const fetchContexts = async () => {
    const org_id = SessionService.ORGANIZATION;
    const project_id = bxProjectId(); //SessionService.PROJECT ?? SessionService.ORGANIZATION;
    if (!org_id) return;
    setContextsLoading(true);
    setContextsError(null);
    try {
      const res = await ObexRequestHandler.get(
        `/get_chat_contexts?org_id=${org_id}&project_id=${project_id}`
      );
      const rows: any[] = res?.data ?? res ?? [];

      const storedTypes = JSON.parse(localStorage.getItem('bx-context-types') || '{}');

      setContexts(prev => {
        // ─── IDs activos en memoria (por si el componente sigue montado) ───
        const memoryActiveIds = new Set(prev.filter(c => c.isActive).map(c => c.id));

        // ─── IDs activos persistidos (sobreviven navegación) ───
        const stored = JSON.parse(localStorage.getItem('bx-active-contexts') || '[]');
        const storedActiveIds = new Set<string>(stored);

        // ─── Combina ambas fuentes ───
        const activeIds = new Set([...Array.from(memoryActiveIds), ...Array.from(storedActiveIds)]);


        return rows.map((r: any) => ({
          id: String(r.id),
          name: r.name,
          description: r.description ?? '',
          isActive: activeIds.has(String(r.id)),
          type: (storedTypes[r.name] as Context['type']) ?? 'text',
          value: r.content || (r.internal ? (r.description ?? '') : ''),
          timestamp: new Date(r.created_at),
          internal: Boolean(r.internal),
        }));
      });
    } catch (err) {
      setContextsError('Could not load contexts. ' + (err?.message ?? ''));
    } finally {
      setContextsLoading(false);
    }
  };

  // Load contexts on initial open or when org/project changes — not on every re-entry
  useEffect(() => {
    // TEMP-SEL_PROJECT: 0 is falsy and would block this. Restore the check once there's a
    // real default project.
    if (!isOpen || !SessionService.ORGANIZATION) return;
    const key = `${SessionService.ORGANIZATION}:${bxProjectId()}`;
    if (loadedForRef.current !== key) {
      loadedForRef.current = key;
      fetchContexts();
      fetchMcpServers();
      fetchHistory();
    }
  }, [isOpen]);

  // ── MCP Servers API helpers ───────────────────────────────────────────────
  const fetchMcpServers = async () => {
    const org_id = SessionService.ORGANIZATION;
    const project_id = bxProjectId(); //SessionService.PROJECT ?? SessionService.ORGANIZATION;
    if (!org_id) return;
    setMcpLoading(true);
    setMcpError(null);
    try {
      const res = await ObexRequestHandler.get(
        `/get_mcp_servers?org_id=${org_id}&project_id=${project_id}`
      );
      const rows: any[] = res?.data ?? res ?? [];
      // Combina estado en memoria + localStorage
      setMcpServers(prev => {
        // ─── IDs activos en memoria ───
        const memoryActiveIds = new Set(prev.filter(m => m.isActive).map(m => m.id));

        // ─── IDs activos persistidos ───
        const stored = JSON.parse(localStorage.getItem('bx-active-mcps') || '[]');
        const storedActiveIds = new Set<string>(stored);

        // ─── Combina ambas fuentes ───
        const activeIds = new Set([...Array.from(memoryActiveIds), ...Array.from(storedActiveIds)]);

        return rows.map((r: any) => ({
          id: String(r.id),
          name: r.name,
          url: r.url,
          description: r.description ?? '',
          isActive: activeIds.has(String(r.id)),
          timestamp: new Date(r.created_at),
          internal: r.internal === true || r.internal === 'true',
          bearer: r.bearer ?? '',
        }));
      });
    } catch (err) {
      setMcpError('Could not load MCP servers. ' + (err?.message ?? ''));
    } finally {
      setMcpLoading(false);
    }
  };

  const fetchHistory = async () => {
    const org_id = SessionService.ORGANIZATION;
    const project_id = bxProjectId();
    // TEMP-SEL_PROJECT
    if (!org_id) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await ObexRequestHandler.get(
        `/get_chat_histories?org_id=${org_id}&project_id=${project_id}`
      );
      const rows: any[] = res?.data ?? res ?? [];
      setHistory(rows.map((r: any) => ({
        id: r.session_id,
        dbId: r.id,
        title: r.title,
        messages: Array.isArray(r.messages)
          ? r.messages.map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) }))
          : [],
        timestamp: new Date(r.updated_at),
        mode: (r.mode ?? 'chat') as HistoryItem['mode'],
      })));
    } catch (err) {
      setHistoryError('Could not load conversation history. ' + (err?.message ?? ''));
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchInternalMcpServers = async () => {
    // TEMP-SEL_PROJECT
    setInternalMcpLoading(true);
    try {
      const res = await ObexRequestHandler.get(
        `/mcp_agents?obex_project_id=${bxProjectId()}`, {}, true
      );
      setInternalMcpServers(res?.data || []);
    } catch {
      setInternalMcpServers([]);
    } finally {
      setInternalMcpLoading(false);
    }
  };

  const handleConnectInternalMcp = async () => {
    if (!selectedInternalMcp) return;
    try {
      await ObexRequestHandler.post('/create_mcp_server', {
        org_id: SessionService.ORGANIZATION,
        project_id: bxProjectId(),
        name: selectedInternalMcp.name,
        url: selectedInternalMcp.endpoint_route || '',
        description: selectedInternalMcp.artifact_description || selectedInternalMcp.description || '',
        internal: true,
      });
      setIsAddingMcp(false);
      setSelectedInternalMcp(null);
      setMcpSourceType('external');
      await fetchMcpServers();
    } catch (err) {
      setMcpError(err?.message ?? 'Could not connect MCP server.');
    }
  };

  const startEditMcp = (mcp: McpServer) => {
    setEditingMcp(mcp);
    setEditMcpName(mcp.name);
    setEditMcpUrl(mcp.url);
    setEditMcpDescription(mcp.description);
    setEditMcpBearer(mcp.bearer ?? '');
    setIsAddingMcp(false);
  };

  const cancelEditMcp = () => {
    setEditingMcp(null);
    setEditMcpName('');
    setEditMcpUrl('');
    setEditMcpDescription('');
    setEditMcpBearer('');
  };

  const handleUpdateMcp = async () => {
    if (!editingMcp || !editMcpName.trim() || !editMcpUrl.trim()) return;
    try {
      await ObexRequestHandler.post('/update_mcp_server', {
        id: Number(editingMcp.id),
        name: editMcpName.trim(),
        url: editMcpUrl.trim(),
        description: editMcpDescription.trim(),
        bearer: editMcpBearer.trim() || undefined,
      });
      setMcpServers(prev => prev.map(m =>
        m.id === editingMcp.id
          ? { ...m, name: editMcpName.trim(), url: editMcpUrl.trim(), description: editMcpDescription.trim() }
          : m
      ));
      cancelEditMcp();
    } catch (err) {
      setMcpError(err?.message ?? 'Could not update MCP server.');
    }
  };

  useEffect(() => {
    const handleUpdateView = () => {
      // TEMP-SEL_PROJECT
      if (!SessionService.ORGANIZATION) return;
      // Force reload for the new org/project
      const key = `${SessionService.ORGANIZATION}:${bxProjectId()}`;
      loadedForRef.current = key;
      fetchMcpServers();
      fetchContexts();
    };
    document.addEventListener('updateView', handleUpdateView);
    return () => document.removeEventListener('updateView', handleUpdateView);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (activeTab === 'contexts') {
      setContexts(items => {
        const oi = items.findIndex(i => i.id === active.id);
        const ni = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oi, ni);
      });
    } else if (activeTab === 'mcp') {
      setMcpServers(items => {
        const oi = items.findIndex(i => i.id === active.id);
        const ni = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oi, ni);
      });
    }
  };

  // Resize
  /*useEffect(() => {
    if (!isResizing) return;
    const onMove = (e: MouseEvent) => {
      const nw = window.innerWidth - e.clientX;
      const max = window.innerWidth - sidebarOffset;
      if (nw < 200 && !isMaximized) { onClose(); setIsResizing(false); return; }
      if (nw > 420) return; // ← límite máximo
      
      setSidebarWidth(Math.max(minWidth, Math.min(nw, max)));
    };
    const onUp = () => { setIsResizing(false); document.body.style.cursor = 'default'; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [isResizing, sidebarOffset]);*/

  // Auto-scroll to bottom when messages update or during streaming
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
    if (nearBottom || isTyping || isStreaming) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, isTyping, isStreaming]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [input]);

  // Scroll button visibility
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      setShowScrollButton(scrollHeight - scrollTop - clientHeight > 50);
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [activeTab]);

  // Click outside dropdowns
  useEffect(() => {
    const onOut = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        llmBtnRef.current && !llmBtnRef.current.contains(e.target as Node)
      )
        setIsLLMDropdownOpen(false);
      if (newChatRef.current && !newChatRef.current.contains(e.target as Node)) setIsNewChatDropdownOpen(false);
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target as Node)) setIsTypeDropdownOpen(false);
      if (addDropdownRef.current && !addDropdownRef.current.contains(e.target as Node)) setIsAddDropdownOpen(false);
    };
    if (isLLMDropdownOpen || isNewChatDropdownOpen || isTypeDropdownOpen || isAddDropdownOpen) {
      document.addEventListener('mousedown', onOut);
    }
    return () => document.removeEventListener('mousedown', onOut);
  }, [isLLMDropdownOpen, isNewChatDropdownOpen, isTypeDropdownOpen, isAddDropdownOpen]);

  useEffect(() => { if (showSearch) searchInputRef.current?.focus(); }, [showSearch]);
  useEffect(() => { if (!isOpen) { setSidebarWidth(400); setIsMaximized(false); } }, [isOpen]);

  useEffect(() => {
    if (!isBrunixMaximized && isMaximized) {
      setIsMaximized(false);
      setSidebarWidth(400);
    }
  }, [isBrunixMaximized]);

  const BRUNIX_FIXED_WIDTH = 400;
  const currentWidth = isMaximized
    ? windowWidth - sidebarOffset
    : BRUNIX_FIXED_WIDTH;

  useEffect(() => {
    if (isOpen && onWidthChange) onWidthChange(currentWidth);
    else if (!isOpen && onWidthChange) onWidthChange(0);
  }, [isOpen, currentWidth]);

  // Auto-save to history (local state + backend)
  useEffect(() => {
    if (messages.length <= 1) return;
    const title = (messages.find(m => m.role === 'user')?.content.slice(0, 40) ?? 'New Conversation') + '...';
    setHistory(prev => {
      const idx = prev.findIndex(i => i.id === currentChatId);
      const item: HistoryItem = { id: currentChatId, title, messages: [...messages], timestamp: new Date(), mode: chatMode, dbId: prev[idx]?.dbId };
      if (idx >= 0) { const h = [...prev]; h[idx] = item; return h; }
      return [item, ...prev];
    });
    const org_id = SessionService.ORGANIZATION;
    const project_id = bxProjectId();
    // TEMP-SEL_PROJECT
    if (org_id) {
      ObexRequestHandler.post('/save_chat_history', {
        org_id, project_id, session_id: currentChatId, title, messages, mode: chatMode,
      }).catch(() => { });
    }
  }, [messages, currentChatId, chatMode]);

  const sanitizeMarkdown = (text: string): string => {
    if (!text) return text;
    let out = text.replace(
      /<code\s+block=["']?([^"'\s>]*)["']?>([\s\S]*?)<\/code>/gi,
      (_: string, lang: string, body: string) => `\n\`\`\`${lang || ''}\n${body.trim()}\n\`\`\`\n`,
    );
    out = out.replace(/<code>([\s\S]*?)<\/code>/gi, (_: string, body: string) => `\`${body.trim()}\``);
    return out;
  };

  // Search matches
  useEffect(() => {
    if (!searchQuery.trim()) { setMatches([]); setCurrentMatchIndex(-1); return; }
    const q = searchQuery.toLowerCase();
    const countIn = (text: string) => {
      const c = sanitizeMarkdown(text || '').toLowerCase();
      let n = 0, pos = c.indexOf(q);
      while (pos !== -1) { n++; pos = c.indexOf(q, pos + q.length); }
      return n;
    };
    const found: { messageId: string }[] = [];
    messages.forEach(msg => {
      if (msg.parts) {
        // Text parts are only rendered once the message is revealed.
        msg.parts.forEach(p => {
          if (p.type === 'text' && p.content) {
            const n = countIn(p.content);
            for (let k = 0; k < n; k++) found.push({ messageId: msg.id });
          }
        });
      } else if (msg.content) {
        const n = countIn(msg.content);
        for (let k = 0; k < n; k++) found.push({ messageId: msg.id });
      }
    });
    setMatches(found);
    setCurrentMatchIndex(found.length > 0 ? 0 : -1);
  }, [searchQuery, messages]);

  const scrollToMatch = (idx: number) => {
    matchRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const handleNextMatch = () => {
    if (!matches.length) return;
    const n = (currentMatchIndex + 1) % matches.length;
    setCurrentMatchIndex(n); scrollToMatch(n);
  };
  const handlePrevMatch = () => {
    if (!matches.length) return;
    const p = (currentMatchIndex - 1 + matches.length) % matches.length;
    setCurrentMatchIndex(p); scrollToMatch(p);
  };

  // ─── Highlight de texto con matches ──────────────────────────────────────
  const renderHighlightedText = (text: string, _messageId: string) => {
    if (!searchQuery.trim()) return text;
    const q = searchQuery.toLowerCase();
    const escaped = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) => {
      if (part.length > 0 && part.toLowerCase() === q) {
        const ordinal = matchOrdinalRef.current++;
        const isCur = ordinal === currentMatchIndex;
        return (
          <span
            key={i}
            ref={el => { matchRefs.current[ordinal] = el; }}
            className={`bx-highlight${isCur ? ' bx-highlight--current' : ''}`}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const highlightIn = (children: React.ReactNode, messageId: string) =>
    React.Children.map(children, c => typeof c === 'string' ? renderHighlightedText(c, messageId) : c);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    sendMessage(input.trim());
    setInput('');
  };

  const handleNewChat = (mode: 'chat' | 'codex' | 'builder' | 'architect' = 'chat') => {
    reset();
    setChatMode(mode);
    setCurrentChatId(Date.now().toString());
    setInput('');
    setMessages([{
      id: Date.now().toString(),
      // Same as the initial greeting: 'system' renders through SystemMessage.
      role: 'system',
      content: `Hola, soy **Emi** en modo **${mode.toUpperCase()}**. ¿En qué puedo ayudarte?`,
      timestamp: new Date(),
      isWelcome: true,
    }]);
    setActiveTab('chat');
  };

  const loadHistory = (item: HistoryItem) => { setCurrentChatId(item.id); setMessages(item.messages); setChatMode(item.mode); setActiveTab('chat'); };
  const deleteHistory = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setHistoryToDelete(id);
  };
  const confirmDeleteHistory = () => {
    if (!historyToDelete) return;
    const id = historyToDelete;
    const item = history.find(i => i.id === id);
    setHistory(p => p.filter(i => i.id !== id));
    if (currentChatId === id) handleNewChat();
    if (item?.dbId) ObexRequestHandler.post('/delete_chat_history', { id: item.dbId }).catch(() => { });
    setHistoryToDelete(null);
  };
  const copyHistory = (item: HistoryItem, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(item.messages.map(m => `${m.role.toUpperCase()}: ${m.content}`).join('\n\n'));
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleAddContext = async () => {
    if (!newContextName.trim()) return;
    const org_id = SessionService.ORGANIZATION || 1;
    const project_id = bxProjectId(); //SessionService.PROJECT ?? SessionService.ORGANIZATION;

    try {
      await ObexRequestHandler.post('/create_chat_context', {
        org_id,
        project_id,
        name: newContextName.trim(),
        description: newContextDescription.trim(),
        content: newContextValue,
        internal: false
      });

      // ─── Guarda el tipo por nombre en localStorage ───
      const stored = JSON.parse(localStorage.getItem('bx-context-types') || '{}');
      stored[newContextName.trim()] = newContextType;
      localStorage.setItem('bx-context-types', JSON.stringify(stored));

      setNewContextName('');
      setNewContextDescription('');
      setNewContextValue('');
      setSelectedContextFileName('');
      setIsAddingContext(false);
      await fetchContexts();          // refresh list from backend
    } catch (err) {
      // surface the error inline — 409 means duplicate name
      setContextsError(err?.message ?? 'Could not create context.');
    }
  };

  const { getRootProps: getContextFileRootProps, getInputProps: getContextFileInputProps } = useDropzone({
    onDrop: (files) => {
      const file = files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = ev => {
        setNewContextValue(ev.target?.result as string);
        setSelectedContextFileName(file.name);
        if (!newContextName.trim()) setNewContextName(file.name.replace(/\.[^/.]+$/, ''));
      };
      reader.readAsText(file);
    },
    accept: BX_CONTEXT_FILE_ACCEPT,
    multiple: false,
  });

  const fetchInternalContexts = async () => {
    // TEMP-SEL_PROJECT
    setInternalContextsLoading(true);
    try {
      const res = await ObexRequestHandler.get(
        `/mcp_contexts?obex_project_id=${bxProjectId()}`, {}, true
      );
      const rows: any[] = res?.data ?? res ?? [];
      setInternalContexts(rows.filter((r: any) => r.active !== false));
    } catch {
      setInternalContexts([]);
    } finally {
      setInternalContextsLoading(false);
    }
  };

  const handleConnectInternalContext = async () => {
    if (!selectedInternalContext) return;
    try {
      await ObexRequestHandler.post('/create_chat_context', {
        org_id: SessionService.ORGANIZATION,
        project_id: bxProjectId(),
        name: selectedInternalContext.name,
        description: selectedInternalContext.description ?? '',
        content: '',
        internal: true,
      });
      setIsAddingContext(false);
      setSelectedInternalContext(null);
      setContextSourceType('external');
      await fetchContexts();
    } catch (err) {
      setContextsError(err?.message ?? 'Could not connect context.');
    }
  };

  const startEditContext = (ctx: Context) => {
    setEditingContext(ctx);
    setEditContextName(ctx.name);
    setEditContextDescription(ctx.description);
    setIsAddingContext(false);
  };

  const cancelEditContext = () => {
    setEditingContext(null);
    setEditContextName('');
    setEditContextDescription('');
  };

  const handleUpdateContext = async () => {
    if (!editingContext || !editContextName.trim()) return;
    try {
      await ObexRequestHandler.post('/update_chat_context', {
        id: Number(editingContext.id),
        name: editContextName.trim(),
        description: editContextDescription.trim(),
      });
      cancelEditContext();
      await fetchContexts();
    } catch (err) {
      setContextsError(err?.message ?? 'Could not update context.');
    }
  };

  // isActive is purely local UI state (not persisted in the backend)
  const toggleContext = (id: string) =>
    setContexts(p => {
      const updated = p.map(c => c.id === id ? { ...c, isActive: !c.isActive } : c);
      // ─── Guarda IDs activos para sobrevivir navegación ───
      const activeIds = updated.filter(c => c.isActive).map(c => c.id);
      localStorage.setItem('bx-active-contexts', JSON.stringify(activeIds));
      return updated;
    });

  // ─── Abre el modal de confirmación ───────────────────────────────────────────
  // TEMP-DELETE-MODAL: native confirm(), same as AuthorsView/BooksView/GroupsView. Swap for
  // a proper modal once there's one in this project.
  const deleteContext = (id: string) => {
    setContextToDelete(id);
  };

  // ─── Ejecuta el borrado tras confirmación ────────────────────────────────────
  const handleConfirmDeleteContext = async (idArg?: string) => {
    const target = idArg ?? contextToDelete;
    if (!target) return;
    const snapshot = contexts;

    // ─── Limpia el tipo guardado en localStorage ───
    const ctxName = contexts.find(c => c.id === target)?.name;
    if (ctxName) {
      const stored = JSON.parse(localStorage.getItem('bx-context-types') || '{}');
      delete stored[ctxName];
      localStorage.setItem('bx-context-types', JSON.stringify(stored));
    }

    setContexts(p => p.filter(c => c.id !== target));
    setContextDeleteOpen(false);
    setContextToDelete(null);
    try {
      await ObexRequestHandler.post('/delete_chat_context', { id: Number(target) });
    } catch (err) {
      setContexts(snapshot);
      setContextsError(err?.message ?? 'Could not delete context.');
    }
  };

  const handleAddMcp = async () => {
    if (!newMcpName.trim() || !newMcpUrl.trim()) return;
    const org_id = SessionService.ORGANIZATION;
    const project_id = bxProjectId(); //SessionService.PROJECT ?? SessionService.ORGANIZATION;
    try {
      await ObexRequestHandler.post('/create_mcp_server', {
        org_id,
        project_id,
        name: newMcpName.trim(),
        url: newMcpUrl.trim(),
        description: newMcpDescription.trim(),
        bearer: newMcpBearer.trim(),
        internal: false,
      });
      setIsAddingMcp(false);
      setNewMcpName('');
      setNewMcpUrl('');
      setNewMcpDescription('');
      setNewMcpBearer('');
      await fetchMcpServers();
    } catch (err) {
      setMcpError(err?.message ?? 'Could not create MCP server.');
    }
  };

  // isActive is purely local UI state (which servers are attached to the session)
  const toggleMcp = (id: string) =>
    setMcpServers(p => {
      const updated = p.map(m => m.id === id ? { ...m, isActive: !m.isActive } : m);
      // ─── Guarda IDs activos para sobrevivir navegación y sesiones ───
      const activeIds = updated.filter(m => m.isActive).map(m => m.id);
      localStorage.setItem('bx-active-mcps', JSON.stringify(activeIds));
      return updated;
    });

  // TEMP-DELETE-MODAL
  const deleteMcp = (id: string) => {
    setMcpToDelete(id);
  };
  // ─── Ejecuta el borrado de MCP tras confirmación ─────────────────────────────
  const handleConfirmDeleteMcp = async (idArg?: string) => {
    const target = idArg ?? mcpToDelete;
    if (!target) return;
    const snapshot = mcpServers;
    setMcpServers(p => p.filter(m => m.id !== target));
    setMcpDeleteOpen(false);
    setMcpToDelete(null);
    try {
      await ObexRequestHandler.post('/delete_mcp_server', { id: Number(target) });
    } catch (err) {
      setMcpServers(snapshot);
      setMcpError(err?.message ?? 'Could not delete MCP server.');
    }
  };

  const toggleMaximize = () => {
    if (isMaximized) {
      setIsMaximized(false);
      setIsBrunixMaximized(false);
      setIsSidebarCollapsed(sidebarStateBeforeMaximize);
    } else {
      setSidebarStateBeforeMaximize(isSidebarCollapsed);
      setIsMaximized(true);
      setIsBrunixMaximized(true);
    }
  };

  const handleFeedback = (id: string, type: 'like' | 'dislike') => setMessages(p => p.map(m => m.id === id ? { ...m, feedback: m.feedback === type ? null : type } : m));

  const TypeIcon = typeOptions.find(t => t.id === newContextType)?.icon ?? FileText;

  if (isOpen) hasOpenedOnce.current = true;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.aside
            className="bx-aside"
            // DashboardPage envuelve todo en un div con onClick={closeAllMenus},
            // que cierra este panel. Sin esto, cualquier clic dentro lo cierra.
            onClick={(e) => e.stopPropagation()}
            initial={{ x: '100%', width: currentWidth }}
            animate={{ x: 0, width: currentWidth }}
            exit={{ x: '100%' }}
            transition={{
              x: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
              width: { duration: 0 }
            }}
          >
            {/* Header */}
            <div className="bx-header">
              <div className="bx-header-brand">
                <div className="bx-header-icon"><Bot size={18} /></div>
                <span className="bx-header-title">Emi 3.15</span>
                <span className="bx-beta-label">BETA</span>
              </div>
              <div className="bx-header-actions">
                <button
                  className={`bx-icon-btn bx-icon-btn--search${showSearch ? ' bx-icon-btn--active' : ''}`}
                  onClick={() => setShowSearch(!showSearch)}
                  title="Search"
                  disabled={activeTab !== 'chat'}
                ><Search size={18} /></button>

                <div className="bx-newchat-dropdown" ref={newChatRef}>
                  <button className={`bx-icon-btn${isNewChatDropdownOpen ? ' bx-icon-btn--active' : ''}`} onClick={() => setIsNewChatDropdownOpen(!isNewChatDropdownOpen)} title="New Chat"><Plus size={18} /></button>
                  <AnimatePresence>
                    {isNewChatDropdownOpen && (
                      <motion.div className="bx-newchat-menu" initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }}>
                        {modeOptions.map(o => (
                          <button key={o.id} className="bx-newchat-option" onClick={() => { handleNewChat(o.id as any); setIsNewChatDropdownOpen(false); }}>
                            <o.icon size={14} style={{ color: 'var(--bx-brand)' }} />{o.name}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/*<button className="bx-icon-btn" title="Settings" onClick={() => {}}><Settings size={18} /></button>*/}
                <button className="bx-icon-btn" onClick={toggleMaximize} title={isMaximized ? 'Restore' : 'Maximize'}>
                  {isMaximized ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                </button>
                <button className="bx-icon-btn" onClick={onClose} title="Close"><X size={18} /></button>
              </div>
            </div>

            {/* Search bar */}
            <AnimatePresence>
              {showSearch && (
                <motion.div className="bx-search-bar" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                  <div className="bx-search-bar-inner">
                    <div className="bx-search-field">
                      <span className="bx-search-field-icon"><Search size={14} /></span>
                      <input ref={searchInputRef} type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search in conversation..." className="bx-search-input" />
                    </div>
                    {matches.length > 0 && (
                      <div className="bx-search-nav">
                        <span className="bx-search-count">{currentMatchIndex + 1}/{matches.length}</span>
                        <button className="bx-search-nav-btn" onClick={handlePrevMatch}><ChevronDown size={14} className="bx-chevron-up" /></button>
                        <button className="bx-search-nav-btn" onClick={handleNextMatch}><ChevronDown size={14} /></button>
                      </div>
                    )}
                    <button className="bx-search-nav-btn" onClick={() => { setShowSearch(false); setSearchQuery(''); }}><X size={14} /></button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Tabs */}
            <div className="bx-tabs">
              {(['chat', 'history', 'contexts', 'mcp'] as const).map(tab => (
                <button key={tab} className={`bx-tab${activeTab === tab ? ' bx-tab--active' : ''}`} onClick={() => { setActiveTab(tab); if (tab !== 'chat') { setShowSearch(false); setSearchQuery(''); } }}>
                  {tab === 'chat' ? chatMode : tab === 'mcp' ? 'MCP Servers' : tab}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="bx-content">
              <div className="bx-scroll" ref={scrollRef}>

                {/* ── Chat ── */}
                {activeTab === 'chat' && (
                  <div className="bx-messages" ref={scrollRef}>
                    <BrunixMessageList
                      messages={messages}
                      isStreaming={isStreaming}
                      streamingMsgId={streamingMsgId}
                      llmCalling={llmCalling}
                      streamJustFinished={streamJustFinished}
                      lastStepReached={lastStepReached}
                      isTyping={isTyping}
                      onCancelStream={() => {
                        cancelStream();
                      }}
                      onConfirmAction={(runId, confirmed) => {
                        confirmAction(runId, confirmed);
                      }}
                      onSkipAction={(runId) => {
                        skipAction(runId);
                      }}
                      onRetryAction={(runId, parsedArgs) => {
                        retryAction(runId, parsedArgs);
                      }}
                      onFeedback={handleFeedback}
                      isCompact={true}
                      searchQuery={searchQuery}
                      currentMatchIndex={currentMatchIndex}
                      slotInputs={slotInputs}
                      setSlotInputs={setSlotInputs}
                      stepSelections={stepSelections}
                      setStepSelections={setStepSelections}
                      retryArgsText={retryArgsText}
                      setRetryArgsText={setRetryArgsText}
                    />
                  </div>
                )}

                {/* ── History ── */}
                {activeTab === 'history' && (
                  <div className="bx-history-list">
                    {historyLoading ? (
                      <div className="bx-empty">
                        <div className="bx-typing-dots" style={{ justifyContent: 'center', padding: '24px 0' }}>
                          <span className="bx-typing-dot" /><span className="bx-typing-dot" /><span className="bx-typing-dot" />
                        </div>
                      </div>
                    ) : historyError ? (
                      <div className="bx-empty">
                        <div className="bx-empty-icon"><AlertCircle size={32} /></div>
                        <p className="bx-empty-text" style={{ color: 'var(--bx-error, #ff6b6b)', fontSize: '12px', textAlign: 'center', padding: '0 16px' }}>{historyError}</p>
                        <button className="bx-form-btn-cancel" style={{ marginTop: 8 }} onClick={() => { setHistoryError(null); fetchHistory(); }}>Retry</button>
                      </div>
                    ) : history.length > 0 ? history.map(item => (
                      <div key={item.id} className={`bx-history-item${currentChatId === item.id ? ' bx-history-item--active' : ''}`} onClick={() => loadHistory(item)}>
                        <div className="bx-history-item-inner">
                          <div className="bx-history-item-info">
                            <div className="bx-history-item-badges">
                              <span className={`bx-badge bx-badge--${item.mode}`}>{item.mode}</span>
                              <span className="bx-history-item-date">{item.timestamp.toLocaleDateString()} {item.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                            <p className="bx-history-item-title">{item.title}</p>
                            <p className="bx-history-item-count">{item.messages.length} messages</p>
                          </div>
                          <div className="bx-history-item-actions">
                            <button className="bx-history-action-btn" onClick={e => copyHistory(item, e)}>
                              {copiedId === item.id ? <Check size={14} className="bx-check-green" /> : <Copy size={14} />}
                            </button>
                            <button className="bx-history-action-btn bx-history-action-btn--danger" onClick={e => deleteHistory(item.id, e)}><Trash2 size={14} /></button>
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="bx-empty"><div className="bx-empty-icon"><History size={32} /></div><p className="bx-empty-text">No history yet</p><button className="bx-form-btn-cancel" style={{ marginTop: 8 }} onClick={fetchHistory}>Refresh</button></div>
                    )}
                  </div>
                )}

                {/* ── Contexts ── */}
                {activeTab === 'contexts' && (
                  <div>
                    <AnimatePresence>
                      {isAddingContext && (
                        <motion.div className="bx-add-form" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                          <div className="bx-add-form-label"><FileText size={14} />New Context</div>

                          {/* ── Source type tabs ── */}
                          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 12 }}>
                            {(['external', 'internal'] as const).map(type => (
                              <button
                                key={type}
                                type="button"
                                onClick={() => {
                                  setContextSourceType(type);
                                  setSelectedInternalContext(null);
                                  if (type === 'internal') fetchInternalContexts();
                                }}
                                style={{
                                  padding: '5px 12px',
                                  border: 'none',
                                  borderBottom: contextSourceType === type ? '2px solid var(--bx-brand, #ef7c4d)' : '2px solid transparent',
                                  background: 'transparent',
                                  color: contextSourceType === type ? 'var(--bx-brand, #ef7c4d)' : 'rgba(255,255,255,0.35)',
                                  fontSize: 12,
                                  fontWeight: contextSourceType === type ? 600 : 400,
                                  cursor: 'pointer',
                                  marginBottom: -1,
                                  transition: 'all 0.15s',
                                  textTransform: 'capitalize',
                                }}
                              >
                                {type === 'external' ? 'External' : 'Internal'}
                              </button>
                            ))}
                          </div>

                          {/* ── External form ── */}
                          {contextSourceType === 'external' && (
                            <>
                              <div className="bx-add-form-fields">
                                <input type="text" value={newContextName} onChange={e => setNewContextName(e.target.value)} placeholder="Context name..." className="bx-form-input" />
                                <textarea value={newContextDescription} onChange={e => setNewContextDescription(e.target.value)} placeholder="Description (optional)..." rows={5} className="bx-form-textarea" />
                                {/* Type selector */}
                                <div className="bx-type-dropdown" ref={typeDropdownRef}>
                                  <button className="bx-type-dropdown-btn" onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}>
                                    <span className="bx-type-dropdown-btn-label">
                                      <TypeIcon size={14} style={{ color: 'var(--bx-brand)' }} />
                                      <span style={{ textTransform: 'capitalize' }}>{newContextType}</span>
                                    </span>
                                    <ChevronDown size={14} className={`bx-chevron${isTypeDropdownOpen ? ' bx-chevron--open' : ''}`} />
                                  </button>
                                  <AnimatePresence>
                                    {isTypeDropdownOpen && (
                                      <motion.div className="bx-type-dropdown-menu" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}>
                                        {typeOptions.map(t => (
                                          <button key={t.id} className="bx-type-dropdown-option" onClick={() => { setNewContextType(t.id as any); setIsTypeDropdownOpen(false); setNewContextValue(''); setSelectedContextFileName(''); }}>
                                            <t.icon size={14} />{t.name}
                                          </button>
                                        ))}
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                                {/* Conditional fields */}
                                {newContextType === 'text' && <textarea value={newContextValue} onChange={e => setNewContextValue(e.target.value)} placeholder="Enter context text here..." rows={6} className="bx-form-textarea" />}
                                {newContextType === 'file' && (
                                  <div className="bx-file-row">
                                    <input
                                      type="text"
                                      value={selectedContextFileName || newContextValue}
                                      onChange={e => { setNewContextValue(e.target.value); setSelectedContextFileName(''); }}
                                      placeholder="File path or browse for a file..."
                                      className="bx-form-input"
                                      style={{ flex: 1 }}
                                    />
                                    <button type="button" className="bx-file-browse-btn" {...getContextFileRootProps()} title={`Browse (${BX_CONTEXT_FILE_ACCEPT['text/*'].join(', ')})`}>
                                      <input {...getContextFileInputProps()} />
                                      <Folder size={14} />
                                    </button>
                                  </div>
                                )}
                                {newContextType === 'url' && <input type="text" value={newContextValue} onChange={e => setNewContextValue(e.target.value)} placeholder="https://example.com" className="bx-form-input" />}
                              </div>
                              <div className="bx-form-actions">
                                <button className="bx-form-btn-cancel" onClick={() => { setIsAddingContext(false); setNewContextName(''); setNewContextDescription(''); setNewContextValue(''); setSelectedContextFileName(''); setContextSourceType('external'); }}>Cancel</button>
                                <button className="bx-form-btn-submit" onClick={handleAddContext} disabled={!newContextName.trim()}>Create</button>
                              </div>
                            </>
                          )}

                          {/* ── Internal list ── */}
                          {contextSourceType === 'internal' && (
                            <>
                              <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
                                {internalContextsLoading ? (
                                  <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                                    <div className="bx-typing-dots">
                                      <span className="bx-typing-dot" /><span className="bx-typing-dot" /><span className="bx-typing-dot" />
                                    </div>
                                  </div>
                                ) : internalContexts.length === 0 ? (
                                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', textAlign: 'center', padding: '12px 0' }}>No internal contexts found</p>
                                ) : internalContexts.map((ctx: any) => {
                                  const isSelected = selectedInternalContext?.id === ctx.id;
                                  return (
                                    <button
                                      key={ctx.id}
                                      type="button"
                                      onClick={() => setSelectedInternalContext(isSelected ? null : ctx)}
                                      style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                                        padding: '8px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                        background: isSelected ? 'rgba(239,124,77,0.12)' : 'rgba(255,255,255,0.04)',
                                        outline: isSelected ? '1px solid rgba(239,124,77,0.4)' : '1px solid transparent',
                                        textAlign: 'left', transition: 'all 0.15s',
                                      }}
                                    >
                                      <span style={{ fontSize: 12, fontWeight: 600, color: isSelected ? 'var(--bx-brand, #ef7c4d)' : 'rgba(255,255,255,0.85)' }}>
                                        {ctx.name}
                                      </span>
                                      {ctx.description && (
                                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                                          {ctx.description}
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="bx-form-actions">
                                <button className="bx-form-btn-cancel" onClick={() => { setIsAddingContext(false); setSelectedInternalContext(null); setContextSourceType('external'); }}>Cancel</button>
                                <button className="bx-form-btn-submit" onClick={handleConnectInternalContext} disabled={!selectedInternalContext}>Connect</button>
                              </div>
                            </>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* ── Edit form ── */}
                    <AnimatePresence>
                      {editingContext && (
                        <motion.div className="bx-add-form" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                          <div className="bx-add-form-label"><Pencil size={14} />Edit Context</div>
                          <div className="bx-add-form-fields">
                            <input type="text" value={editContextName} onChange={e => setEditContextName(e.target.value)} placeholder="Context name..." className="bx-form-input" />
                            <textarea value={editContextDescription} onChange={e => setEditContextDescription(e.target.value)} placeholder="Description (optional)..." rows={3} className="bx-form-textarea" />
                          </div>
                          <div className="bx-form-actions">
                            <button className="bx-form-btn-cancel" onClick={cancelEditContext}>Cancel</button>
                            <button className="bx-form-btn-submit" onClick={handleUpdateContext} disabled={!editContextName.trim()}>Save</button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div className="bx-list">
                      {contextsLoading ? (
                        <div className="bx-empty">
                          <div className="bx-typing-dots" style={{ justifyContent: 'center', padding: '24px 0' }}>
                            <span className="bx-typing-dot" /><span className="bx-typing-dot" /><span className="bx-typing-dot" />
                          </div>
                        </div>
                      ) : contextsError ? (
                        <div className="bx-empty">
                          <div className="bx-empty-icon"><AlertCircle size={32} /></div>
                          <p className="bx-empty-text" style={{ color: 'var(--bx-error, #ff6b6b)', fontSize: '12px', textAlign: 'center', padding: '0 16px' }}>{contextsError}</p>
                          <button className="bx-form-btn-cancel" style={{ marginTop: 8 }} onClick={() => { setContextsError(null); fetchContexts(); }}>Retry</button>
                        </div>
                      ) : contexts.length > 0 ? (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis, restrictToParentElement]}>
                          <SortableContext items={contexts.map(c => c.id)} strategy={verticalListSortingStrategy}>
                            {contexts.map(ctx => <SortableContextItem key={ctx.id} ctx={ctx} toggleContext={toggleContext} deleteContext={deleteContext} editContext={startEditContext} />)}
                          </SortableContext>
                        </DndContext>
                      ) : (
                        <div className="bx-empty"><div className="bx-empty-icon"><FileText size={32} /></div><p className="bx-empty-text" style={{ padding: '0 16px' }}>No contexts defined</p><button className="bx-form-btn-cancel" style={{ marginTop: 8 }} onClick={fetchContexts}>Refresh</button></div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── MCP ── */}
                {activeTab === 'mcp' && (
                  <div>
                    <AnimatePresence>
                      {isAddingMcp && (
                        <motion.div className="bx-add-form" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                          <div className="bx-add-form-label"><Terminal size={14} />New MCP Server</div>

                          {/* ── Source type tabs ── */}
                          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 12 }}>
                            {(['external', 'internal'] as const).map(type => (
                              <button
                                key={type}
                                type="button"
                                onClick={() => {
                                  setMcpSourceType(type);
                                  setSelectedInternalMcp(null);
                                  if (type === 'internal') fetchInternalMcpServers();
                                }}
                                style={{
                                  padding: '5px 12px',
                                  border: 'none',
                                  borderBottom: mcpSourceType === type ? '2px solid var(--bx-brand, #ef7c4d)' : '2px solid transparent',
                                  background: 'transparent',
                                  color: mcpSourceType === type ? 'var(--bx-brand, #ef7c4d)' : 'rgba(255,255,255,0.35)',
                                  fontSize: 12,
                                  fontWeight: mcpSourceType === type ? 600 : 400,
                                  cursor: 'pointer',
                                  marginBottom: -1,
                                  transition: 'all 0.15s',
                                  textTransform: 'capitalize',
                                }}
                              >
                                {type === 'external' ? 'External' : 'Internal'}
                              </button>
                            ))}
                          </div>

                          {/* ── External form ── */}
                          {mcpSourceType === 'external' && (
                            <>
                              <div className="bx-add-form-fields">
                                <input type="text" value={newMcpName} onChange={e => setNewMcpName(e.target.value)} placeholder="Server name..." className="bx-form-input" />
                                <input type="text" value={newMcpUrl} onChange={e => setNewMcpUrl(e.target.value)} placeholder="Server URL (http://...)" className="bx-form-input" />
                                <input type="text" value={newMcpBearer} onChange={e => setNewMcpBearer(e.target.value)} placeholder="Bearer token (optional)..." className="bx-form-input" />
                                <textarea value={newMcpDescription} onChange={e => setNewMcpDescription(e.target.value)} placeholder="Description (optional)..." rows={3} className="bx-form-textarea" />
                              </div>
                              <div className="bx-form-actions">
                                <button className="bx-form-btn-cancel" onClick={() => { setIsAddingMcp(false); setNewMcpName(''); setNewMcpUrl(''); setNewMcpDescription(''); setNewMcpBearer(''); setMcpSourceType('external'); }}>Cancel</button>
                                <button className="bx-form-btn-submit" onClick={handleAddMcp} disabled={!newMcpName.trim() || !newMcpUrl.trim()}>Connect</button>
                              </div>
                            </>
                          )}

                          {/* ── Internal list ── */}
                          {mcpSourceType === 'internal' && (
                            <>
                              <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
                                {internalMcpLoading ? (
                                  <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                                    <div className="bx-typing-dots">
                                      <span className="bx-typing-dot" /><span className="bx-typing-dot" /><span className="bx-typing-dot" />
                                    </div>
                                  </div>
                                ) : internalMcpServers.length === 0 ? (
                                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', textAlign: 'center', padding: '12px 0' }}>No internal MCP servers found</p>
                                ) : internalMcpServers.map((srv: any) => {
                                  const isSelected = selectedInternalMcp?.id === srv.id;
                                  return (
                                    <button
                                      key={srv.id}
                                      type="button"
                                      onClick={() => setSelectedInternalMcp(isSelected ? null : srv)}
                                      style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                                        padding: '8px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                        background: isSelected ? 'rgba(239,124,77,0.12)' : 'rgba(255,255,255,0.04)',
                                        outline: isSelected ? '1px solid rgba(239,124,77,0.4)' : '1px solid transparent',
                                        textAlign: 'left', transition: 'all 0.15s',
                                      }}
                                    >
                                      <span style={{ fontSize: 12, fontWeight: 600, color: isSelected ? 'var(--bx-brand, #ef7c4d)' : 'rgba(255,255,255,0.85)' }}>
                                        {srv.name}
                                      </span>
                                      {srv.endpoint_route && (
                                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace', marginTop: 2 }}>
                                          {srv.endpoint_route}
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="bx-form-actions">
                                <button className="bx-form-btn-cancel" onClick={() => { setIsAddingMcp(false); setSelectedInternalMcp(null); setMcpSourceType('external'); }}>Cancel</button>
                                <button className="bx-form-btn-submit" onClick={handleConnectInternalMcp} disabled={!selectedInternalMcp}>Connect</button>
                              </div>
                            </>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* ── Edit form ── */}
                    <AnimatePresence>
                      {editingMcp && (
                        <motion.div className="bx-add-form" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                          <div className="bx-add-form-label"><Pencil size={14} />Edit MCP Server</div>
                          <div className="bx-add-form-fields">
                            <input type="text" value={editMcpName} onChange={e => setEditMcpName(e.target.value)} placeholder="Server name..." className="bx-form-input" />
                            <input type="text" value={editMcpUrl} onChange={e => setEditMcpUrl(e.target.value)} placeholder="Server URL (http://...)" className="bx-form-input" />
                            <textarea value={editMcpDescription} onChange={e => setEditMcpDescription(e.target.value)} placeholder="Description (optional)..." rows={2} className="bx-form-textarea" />
                            <input type="password" value={editMcpBearer} onChange={e => setEditMcpBearer(e.target.value)} placeholder="Bearer token (optional)..." className="bx-form-input" />
                          </div>
                          <div className="bx-form-actions">
                            <button className="bx-form-btn-cancel" onClick={cancelEditMcp}>Cancel</button>
                            <button className="bx-form-btn-submit" onClick={handleUpdateMcp} disabled={!editMcpName.trim() || !editMcpUrl.trim()}>Save</button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div className="bx-list">
                      {mcpLoading ? (
                        <div className="bx-empty">
                          <div className="bx-typing-dots" style={{ justifyContent: 'center', padding: '24px 0' }}>
                            <span className="bx-typing-dot" /><span className="bx-typing-dot" /><span className="bx-typing-dot" />
                          </div>
                        </div>
                      ) : mcpError ? (
                        <div className="bx-empty">
                          <div className="bx-empty-icon"><AlertCircle size={32} /></div>
                          <p className="bx-empty-text" style={{ color: 'var(--bx-error, #ff6b6b)', fontSize: '12px', textAlign: 'center', padding: '0 16px' }}>{mcpError}</p>
                          <button className="bx-form-btn-cancel" style={{ marginTop: 8 }} onClick={() => { setMcpError(null); fetchMcpServers(); }}>Retry</button>
                        </div>
                      ) : mcpServers.length > 0 ? (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis, restrictToParentElement]}>
                          <SortableContext items={mcpServers.map(m => m.id)} strategy={verticalListSortingStrategy}>
                            {mcpServers.map(mcp => <SortableMcpItem key={mcp.id} mcp={mcp} toggleMcp={toggleMcp} deleteMcp={deleteMcp} editMcp={startEditMcp} />)}
                          </SortableContext>
                        </DndContext>
                      ) : (
                        <div className="bx-empty"><div className="bx-empty-icon"><Terminal size={32} /></div><p className="bx-empty-text">No MCP Servers connected</p><button className="bx-form-btn-cancel" style={{ marginTop: 8 }} onClick={fetchMcpServers}>Refresh</button></div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Anchor for auto-scroll */}
              <div ref={messagesEndRef} />

              {/* Scroll to bottom */}
              <AnimatePresence>
                {showScrollButton && (
                  <motion.button className="bx-scroll-btn" onClick={() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} title="Scroll to bottom">
                    <ArrowDown size={16} />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* Presets bar */}
            <div className="bx-presets-bar">
              <div className="bx-add-dropdown" ref={addDropdownRef}>
                <button className={`bx-add-dropdown-btn${isAddDropdownOpen ? ' bx-add-dropdown-btn--open' : ''}`} onClick={() => setIsAddDropdownOpen(!isAddDropdownOpen)} title="Add...">
                  <Plus size={12} className={`bx-add-dropdown-icon${isAddDropdownOpen ? ' bx-add-dropdown-icon--open' : ''}`} />
                </button>
                <AnimatePresence>
                  {isAddDropdownOpen && (
                    <motion.div className="bx-add-dropdown-menu" initial={{ opacity: 0, y: -10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -10, scale: 0.95 }}>
                      {/* TEMP-SEL_PROJECT */}
                      <button className="bx-add-dropdown-option" onClick={() => { setActiveTab('contexts'); setIsAddingContext(true); setIsAddDropdownOpen(false); }}><FileText size={12} style={{ color: 'var(--bx-brand)' }} />Context</button>
                      <button className="bx-add-dropdown-option" onClick={() => { setActiveTab('mcp'); setIsAddingMcp(true); setIsAddDropdownOpen(false); }}><Terminal size={12} style={{ color: 'var(--bx-brand)' }} />MCP Server</button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {contexts.filter(c => c.isActive).map(ctx => (
                <div key={ctx.id} className="bx-preset-tag">
                  <span>{ctx.name}</span>
                  <button className="bx-preset-tag-remove" onClick={e => { e.stopPropagation(); toggleContext(ctx.id); }} title="Deactivate"><X size={10} /></button>
                </div>
              ))}
              {mcpServers.filter(m => m.isActive).map(mcp => (
                <div key={mcp.id} className="bx-preset-tag bx-preset-tag--mcp">
                  <Terminal size={10} style={{ flexShrink: 0 }} />
                  <span>{mcp.name}</span>
                  <button className="bx-preset-tag-remove" onClick={e => { e.stopPropagation(); toggleMcp(mcp.id); }} title="Disconnect"><X size={10} /></button>
                </div>
              ))}
            </div>

            {/* Input area */}
            {activeTab === 'chat' && (
              <div className="bx-input-area">
                <div className="bx-input-box">
                  <form className="bx-input-form" onSubmit={handleSend}>
                    <textarea
                      ref={textareaRef} value={input} onChange={e => setInput(e.target.value)}
                      placeholder="Ask Emi a question or type '/' for commands"
                      className="bx-textarea"
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); } }}
                    />
                    <div className="bx-input-footer">
                      <div className="bx-input-tools">
                        {/* LLM dropdown */}
                        <div className="bx-llm-dropdown" ref={dropdownRef}>
                          <button
                            ref={llmBtnRef}
                            type="button"
                            className={`bx-llm-dropdown-btn${isLLMDropdownOpen ? ' bx-llm-dropdown-btn--open' : ''}`}
                            onClick={() => {
                              if (!isLLMDropdownOpen && llmBtnRef.current) {
                                const rect = llmBtnRef.current.getBoundingClientRect();
                                setLlmDropdownPos({ top: rect.top - 8, left: rect.left });
                              }
                              setIsLLMDropdownOpen(!isLLMDropdownOpen);
                            }}
                          >
                            <Bot size={12} />
                            <span>{selectedLLM}</span>
                            <ChevronDown size={12} className={`bx-chevron${isLLMDropdownOpen ? ' bx-chevron--open' : ''}`} />
                          </button>
                          {isLLMDropdownOpen && createPortal(
                            <div
                              ref={dropdownRef}
                              style={{
                                position: 'fixed',
                                top: llmDropdownPos.top,
                                left: llmDropdownPos.left,
                                transform: 'translateY(-100%)',
                                width: '192px',
                                backgroundColor: 'var(--bx-zinc-950)',
                                border: '1px solid var(--bx-white-10)',
                                borderRadius: '12px',
                                /*boxShadow: '0 25px 50px -12px rgba(0,0,0,0.6)',*/
                                zIndex: 9999,
                                overflow: 'hidden',
                                padding: '4px',
                              }}
                            >
                              {LLMS.map(llm => (
                                <button
                                  key={llm.id}
                                  type="button"
                                  className={`bx-llm-option${selectedLLM === llm.name ? ' bx-llm-option--selected' : ''}`}
                                  onClick={() => { setSelectedLLM(llm.name); setIsLLMDropdownOpen(false); }}
                                >
                                  {llm.icon && (
                                    <llm.icon size={18} style={{ borderRadius: 4, flexShrink: 0, }} />
                                  )}
                                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span className="bx-llm-option-name">{llm.name}</span>
                                    <span className="bx-llm-option-provider">{llm.provider}</span>
                                  </div>
                                </button>
                              ))}
                            </div>,
                            document.body
                          )}
                        </div>
                        {/*<button type="button" className="bx-tool-btn" title="Slash commands"><Slash size={16} /></button>
                    <button type="button" className="bx-tool-btn" title="Variables"><Hash size={16} /></button>
                    <button type="button" className="bx-tool-btn" title="Attach context"><Paperclip size={16} /></button>*/}
                      </div>
                      <button type="submit" className="bx-send-btn" disabled={!input.trim()}><Send size={16} /></button>
                    </div>
                  </form>
                </div>
                <p className="bx-input-disclaimer">Emi AI can make mistakes. Verify important information.</p>
              </div>
            )}
          </motion.aside>
        )}
      </AnimatePresence>
      {pendingConfirmation && (
        <ConfirmationModal
          payload={pendingConfirmation.payload}
          onAccept={() => confirmAction(pendingConfirmation.runId, true)}
          onReject={() => confirmAction(pendingConfirmation.runId, false)}
        />
      )}

      <Modal
        isOpen={!!contextToDelete}
        type="urgent"
        title="Eliminar contexto"
        message={`¿Está seguro de que desea eliminar este contexto?`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={() => handleConfirmDeleteContext()}
        onCancel={() => setContextToDelete(null)}
      />

      <Modal
        isOpen={!!mcpToDelete}
        type="urgent"
        title="Eliminar servidor MCP"
        message={`¿Está seguro de que desea eliminar este servidor MCP?`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={() => handleConfirmDeleteMcp()}
        onCancel={() => setMcpToDelete(null)}
      />

      <Modal
        isOpen={!!historyToDelete}
        type="urgent"
        title="Eliminar conversación"
        message="¿Está seguro de que desea eliminar esta conversación del historial?"
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDeleteHistory}
        onCancel={() => setHistoryToDelete(null)}
      />
    </>
  );
}

// ── Sortable Context Item ─────────────────────────────────────────────────────
function SortableContextItem({ ctx, toggleContext, deleteContext, editContext }: { ctx: any; toggleContext: (id: string) => void; deleteContext: (id: string) => void; editContext: (ctx: any) => void; }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: ctx.id });
  const style: React.CSSProperties = { transform: CSS.Transform.toString(transform as any), transition, zIndex: isDragging ? 100 : 'auto', position: 'relative' as const };
  const icons: Record<string, any> = { file: Paperclip, url: Globe, text: FileText, project: Zap };
  const Icon = icons[ctx.type?.toLowerCase()] ?? FileText;
  return (
    <div ref={setNodeRef} style={style} className={`bx-list-item bx-list-item--${ctx.isActive ? 'active' : 'inactive'}${isDragging ? ' bx-list-item--dragging' : ''}`}>
      <div {...attributes} {...listeners} className="bx-list-item-grip"><GripVertical size={14} /></div>
      <div className={`bx-list-item-icon bx-list-item-icon--${ctx.isActive ? 'active' : 'inactive'}`}><Icon size={16} /></div>
      <div className="bx-list-item-info">
        <h4 className="bx-list-item-name">{ctx.name}</h4>
        <p className="bx-list-item-desc">{ctx.description}</p>
      </div>
      <div className="bx-list-item-actions" onPointerDown={e => e.stopPropagation()}>
        <button className={`bx-list-action-btn bx-list-action-btn--zap-${ctx.isActive ? 'active' : 'inactive'}`} onClick={() => toggleContext(ctx.id)} title={ctx.isActive ? 'Deactivate' : 'Activate'}>
          <Zap size={14} fill={ctx.isActive ? 'currentColor' : 'none'} />
        </button>
        {!ctx.internal && (
          <button className="bx-list-action-btn bx-list-action-btn--pencil" onClick={() => editContext(ctx)} title="Edit"><Pencil size={14} /></button>
        )}
        <button className="bx-list-action-btn bx-list-action-btn--trash" onClick={() => deleteContext(ctx.id)} title="Delete"><Trash2 size={14} /></button>
      </div>
    </div>
  );
}

// ── Sortable MCP Item ─────────────────────────────────────────────────────────
function SortableMcpItem({ mcp, toggleMcp, deleteMcp, editMcp }: {
  mcp: any; toggleMcp: (id: string) => void; deleteMcp: (id: string) => void; editMcp: (mcp: any) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: mcp.id });
  const style: React.CSSProperties = { transform: CSS.Transform.toString(transform as any), transition, zIndex: isDragging ? 100 : 'auto', position: 'relative' as const };
  return (
    <div ref={setNodeRef} style={style} className={`bx-list-item bx-list-item--${mcp.isActive ? 'active' : 'inactive'}${isDragging ? ' bx-list-item--dragging' : ''}`}>
      <div {...attributes} {...listeners} className="bx-list-item-grip"><GripVertical size={14} /></div>
      <div className={`bx-list-item-icon bx-list-item-icon--${mcp.isActive ? 'active' : 'inactive'}`}><Terminal size={16} /></div>
      <div className="bx-list-item-info">
        <h4 className="bx-list-item-name">{mcp.name}</h4>
        <p className="bx-list-item-desc">{mcp.url}</p>
      </div>
      <div className="bx-list-item-actions" onPointerDown={e => e.stopPropagation()}>
        <button className={`bx-list-action-btn bx-list-action-btn--zap-${mcp.isActive ? 'active' : 'inactive'}`} onClick={() => toggleMcp(mcp.id)} title={mcp.isActive ? 'Disconnect' : 'Connect'}>
          <Zap size={14} fill={mcp.isActive ? 'currentColor' : 'none'} />
        </button>
        {!mcp.internal && (
          <button className="bx-list-action-btn bx-list-action-btn--pencil" onClick={() => editMcp(mcp)} title="Edit"><Pencil size={14} /></button>
        )}
        <button className="bx-list-action-btn bx-list-action-btn--trash" onClick={() => deleteMcp(mcp.id)} title="Delete"><Trash2 size={14} /></button>
      </div>
    </div>
  );
}
